import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, FileText, ShieldAlert, ScrollText } from 'lucide-react';

// Sub-components for Sales
import SalesCustomers from './accounting/SalesCustomers';
import SalesOrders from './accounting/SalesOrders';
import SalesInvoices from './accounting/SalesInvoices';
import RecurringInvoices from './accounting/RecurringInvoices';
import DeliveryChallans from './accounting/DeliveryChallans';
import PaymentsReceived from './accounting/PaymentsReceived';
import CreditNotes from './accounting/CreditNotes';

// Sub-components for Purchases
import Vendors from './accounting/Vendors';
import Expenses from './accounting/Expenses';
import RecurringExpenses from './accounting/RecurringExpenses';
import PurchaseOrders from './accounting/PurchaseOrders';
import Bills from './accounting/Bills';
import RecurringBills from './accounting/RecurringBills';
import PaymentsMade from './accounting/PaymentsMade';
import VendorCredits from './accounting/VendorCredits';

// Wallets
import WalletReconciliation from './accounting/WalletReconciliation';
import WalletBalances from './accounting/WalletBalances';
import AuditTrail from './accounting/AuditTrail';

export default function Accounting() {
  const [activeMainTab, setActiveMainTab] = useState('sales');
  const [activeSalesTab, setActiveSalesTab] = useState('customers');
  const [activePurchasesTab, setActivePurchasesTab] = useState('vendors');

  return (
    <div className="space-y-4" data-testid="accounting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Accounting</h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage sales, purchases, invoices, and payments (Last 7 days)
          </p>
        </div>
      </div>

      {/* Main Tabs: Sales & Purchases */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 max-w-3xl">
          <TabsTrigger value="sales" className="flex items-center gap-2" data-testid="tab-sales">
            <TrendingUp className="h-4 w-4" />
            Sales
          </TabsTrigger>
          <TabsTrigger value="purchases" className="flex items-center gap-2" data-testid="tab-purchases">
            <TrendingDown className="h-4 w-4" />
            Purchases
          </TabsTrigger>
          <TabsTrigger value="wallets" className="flex items-center gap-2" data-testid="tab-wallets">
            <ShieldAlert className="h-4 w-4" />
            Wallets
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2" data-testid="tab-audit">
            <ScrollText className="h-4 w-4" />
            Audit Trail
          </TabsTrigger>
        </TabsList>

        {/* ==================== SALES TAB ==================== */}
        <TabsContent value="sales" className="space-y-4">
          {/* Sales Sub-tabs */}
          <Tabs value={activeSalesTab} onValueChange={setActiveSalesTab}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="customers" data-testid="sales-tab-customers">Customers</TabsTrigger>
              <TabsTrigger value="quotes" data-testid="sales-tab-quotes">Quotes</TabsTrigger>
              <TabsTrigger value="orders" data-testid="sales-tab-orders">Sales Orders</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="sales-tab-invoices">Invoices</TabsTrigger>
              <TabsTrigger value="recurring" data-testid="sales-tab-recurring">Recurring</TabsTrigger>
              <TabsTrigger value="challans" data-testid="sales-tab-challans">Challans</TabsTrigger>
              <TabsTrigger value="payments" data-testid="sales-tab-payments">Payments</TabsTrigger>
              <TabsTrigger value="credits" data-testid="sales-tab-credits">Credit Notes</TabsTrigger>
            </TabsList>

            <TabsContent value="customers"><SalesCustomers /></TabsContent>
            <TabsContent value="quotes"><QuotesLink /></TabsContent>
            <TabsContent value="orders"><SalesOrders /></TabsContent>
            <TabsContent value="invoices"><SalesInvoices /></TabsContent>
            <TabsContent value="recurring"><RecurringInvoices /></TabsContent>
            <TabsContent value="challans"><DeliveryChallans /></TabsContent>
            <TabsContent value="payments"><PaymentsReceived /></TabsContent>
            <TabsContent value="credits"><CreditNotes /></TabsContent>
          </Tabs>
        </TabsContent>

        {/* ==================== PURCHASES TAB ==================== */}
        <TabsContent value="purchases" className="space-y-4">
          {/* Purchases Sub-tabs */}
          <Tabs value={activePurchasesTab} onValueChange={setActivePurchasesTab}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="vendors" data-testid="purchases-tab-vendors">Vendors</TabsTrigger>
              <TabsTrigger value="expenses" data-testid="purchases-tab-expenses">Expenses</TabsTrigger>
              <TabsTrigger value="recurring-expenses" data-testid="purchases-tab-rec-expenses">Recurring Exp</TabsTrigger>
              <TabsTrigger value="orders" data-testid="purchases-tab-orders">Purchase Orders</TabsTrigger>
              <TabsTrigger value="bills" data-testid="purchases-tab-bills">Bills</TabsTrigger>
              <TabsTrigger value="recurring-bills" data-testid="purchases-tab-rec-bills">Recurring Bills</TabsTrigger>
              <TabsTrigger value="payments" data-testid="purchases-tab-payments">Payments Made</TabsTrigger>
              <TabsTrigger value="credits" data-testid="purchases-tab-credits">Vendor Credits</TabsTrigger>
            </TabsList>

            <TabsContent value="vendors"><Vendors /></TabsContent>
            <TabsContent value="expenses"><Expenses /></TabsContent>
            <TabsContent value="recurring-expenses"><RecurringExpenses /></TabsContent>
            <TabsContent value="orders"><PurchaseOrders /></TabsContent>
            <TabsContent value="bills"><Bills /></TabsContent>
            <TabsContent value="recurring-bills"><RecurringBills /></TabsContent>
            <TabsContent value="payments"><PaymentsMade /></TabsContent>
            <TabsContent value="credits"><VendorCredits /></TabsContent>
          </Tabs>
        </TabsContent>

        {/* ==================== WALLETS TAB ==================== */}
        <TabsContent value="wallets" className="space-y-4">
          <Tabs defaultValue="balances" className="w-full">
            <TabsList>
              <TabsTrigger value="balances" data-testid="wallets-subtab-balances">Balances</TabsTrigger>
              <TabsTrigger value="reconcile" data-testid="wallets-subtab-reconcile">Reconciliation</TabsTrigger>
            </TabsList>
            <TabsContent value="balances" className="mt-4">
              <WalletBalances />
            </TabsContent>
            <TabsContent value="reconcile" className="mt-4">
              <WalletReconciliation />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ==================== AUDIT TRAIL TAB ==================== */}
        <TabsContent value="audit" className="space-y-4">
          <AuditTrail />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Link to existing Quotations module
function QuotesLink() {
  return (
    <Card className="mt-4">
      <CardContent className="pt-6">
        <div className="text-center py-8">
          <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
          <h3 className="text-lg font-medium mb-2">Quotations Module</h3>
          <p className="text-slate-500 mb-4">
            Quotes are managed in the dedicated Quotations module.
          </p>
          <Button onClick={() => window.location.href = '/quotations'}>
            Go to Quotations
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
